import {Component, ElementRef, Inject, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';
import {MatExpansionPanel} from '@angular/material/expansion';
import {MatMenuTrigger} from '@angular/material/menu';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {OrderLinesTarget} from '@domain/order-lines-target.enum';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleItemInPathGroupStatus} from '@domain/production-schedule/production-schedule-item-in-path-group-status';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {Permission} from '@domain/profile/permission.enum';
import {PropertyValue} from '@domain/property-value';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {CadCamService} from '@infrastructure/cad-cam/cad-cam.service';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {ORDERBOOK, Orderbook} from '@infrastructure/http/orderbook/orderbook';
import {PRODUCTION_ORDERS, ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {PROFILE, Profile} from '@infrastructure/http/profile/profile';
import {OverviewListOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-order-line';
import {GridCreelPreviewOfCreelPositionComponent} from '@presentation/pages/textile-data/creel/add/grid-creel-preview-of-creel-position/grid-creel-preview-of-creel-position.component';
import {PreviewType} from '@presentation/pages/textile-data/creel/add/preview-type';
import {FinishingListViewInformationComponent} from '@presentation/pages/textile-data/finishing/finishing-list-view-information/finishing-list-view-information.component';
import {
  AgGridUtils,
  AssertionUtils,
  BaseComponent,
  CellButtonComponent,
  CellButtonParams,
  ColDefBuilderFactoryService,
  Color,
  convertCommercialUnit,
  convertDentsToWidthInMeter,
  convertPicksToHeightInMeter,
  CrudOverviewButtonConfig,
  DialogBuilderFactoryService,
  EmptyCellRendererComponent,
  EnumUtils,
  GridModel,
  GridOptionsBuilderFactoryService,
  GridTagComponent,
  NumberCellEditorComponent,
  NumberCellEditorParams,
  Priority,
  StringUtils,
  TagColor,
  TagSize,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {CellClickedEvent, CellRendererSelectorResult, ColDef, GridOptions, ICellEditorParams, ICellRendererParams, ITooltipParams, ValueGetterParams, ValueSetterParams} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {clone, round} from 'lodash-es';
import {filter, map, Observable, takeUntil} from 'rxjs';
import {ProductionScheduleOrderLineWithQuantity} from '../../drawing-library/views/order-lines-view/production-schedule-order-line-with-quantity';
import {ProductionSchedulePartiallyCompletedStatusParams} from '../../drawing-library/views/order-lines-view/production-schedule-partially-completed-status-cell-renderer/production-schedule-partially-completed-status-cell-renderer-params.interface';
import {ProductionSchedulePartiallyCompletedStatusCellRendererComponent} from '../../drawing-library/views/order-lines-view/production-schedule-partially-completed-status-cell-renderer/production-schedule-partially-completed-status-cell-renderer.component';
import {EditProductionScheduleItemInPathGroupAction} from '../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group-action';
import {EditProductionScheduleItemInPathGroupComponent} from '../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group.component';
import {ProductionScheduleListviewColumnType} from './production-schedule-listview-column-type';
import {ProductionScheduleListviewColumn} from './production-schedule-listview-column.interface';

type RowData = ProductionScheduleItemInPathGroup;
@Component({
  selector: 'app-production-schedule-listview',
  templateUrl: './production-schedule-listview.component.html',
  styleUrls: ['./production-schedule-listview.component.scss']
})
export class ProductionScheduleListviewComponent extends BaseComponent implements OnInit, OnChanges {
  public static readonly yesLabel = 'GENERAL.CONDITION.YES';
  public static readonly noLabel = 'GENERAL.CONDITION.NO';

  @ViewChild(MatMenuTrigger) public trigger: MatMenuTrigger;
  @ViewChild('quantityInput') public quantityInput: ElementRef<HTMLInputElement>;
  @ViewChild('listviewContainer') public listviewContainer: ElementRef<HTMLElement>;
  @ViewChildren('listViewGrids') public listViewGrids: QueryList<AgGridAngular>;
  @ViewChildren('expansionPanels') public expansionPanels: QueryList<MatExpansionPanel>;

  @Input() public productionSchedule: ProductionSchedule;
  @Input() public commercialUnit: Unit;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public allowProductionScheduleItemsToBeEdited: boolean;
  @Input() public listOfCustomSettings: PropertyValue[];
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public displayTechnicalUnit: boolean;
  @Input() public productionScheduleItemMultiplier: number;

  public showQuantityInput = false;
  public initialProductionSchedule: ProductionSchedule;
  public productionScheduleListviewColumnType = ProductionScheduleListviewColumnType;
  public columns: ProductionScheduleListviewColumn[];
  public visibleColumns: ProductionScheduleListviewColumn[];
  public productionScheduleItemInPathGroupStatus: typeof ProductionScheduleItemInPathGroupStatus = ProductionScheduleItemInPathGroupStatus;
  public gridOptionsForListView: {[pathNumber: number]: GridOptions};
  public selectedColumn: ProductionScheduleListviewColumn;
  public pinnedLeftColumns: ProductionScheduleListviewColumn[] = [];
  public pinnedRightColumns: ProductionScheduleListviewColumn[] = [];
  public orderlines: ProductionScheduleOrderLineWithQuantity[] = [];
  public hasViewEpPermission: boolean = false;

  public readonly productionScheduleItemHeightInPx = 56;
  public readonly classNameForProductionScheduleItem = 'production-schedule-item';
  public readonly classNameForPath = 'path';
  public readonly classNameForColoredYarnSet = 'colored-yarn-sets-panel';
  public readonly classNameForEmptyElements = 'empty';
  public readonly positionOfDialog: PositionOfDialog = PositionOfDialog.RIGHT;
  public readonly permissionToViewBuggyLengthWarning = Permission.VIEW_MIN_MAX_BUGGY;

  private readonly rowHeight = 32;
  private readonly headerHeight = 40;
  private readonly scrollBarHeightInPx = 17;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(ORDERBOOK) private readonly orderbook: Orderbook,
    @Inject(PROFILE) private readonly profile: Profile,
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    private readonly translate: TranslateService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly cadCamService: CadCamService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.hasViewEpPermission = this.authentication.getCurrentSubscription()?.hasPermission(Permission.TEXEDIT_VIEW_EP) ?? false;
    this.initialProductionSchedule = clone(this.productionSchedule);
    this.getDataForProductionScheduleItem();

    const pathNumbers = this.getPathNumbers(this.productionSchedule);
    this.initialiseGridOptionsForListiew(pathNumbers);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('productionSchedule' in changes) {
      this.getDataForProductionScheduleItem();
    }
  }

  public trackProductionScheduleColoredYarnSet(productionScheduleColoredYarnSetIndex: number, productionScheduleColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): number {
    return productionScheduleColoredYarnSet.productionScheduleColoredYarnSet.coloredYarnSet.id;
  }

  public trackProductionSchedulePath(productionSchedulePathIndex: number, productionSchedulePath: ProductionSchedulePath): string {
    return productionSchedulePath.name;
  }

  public getUnitDimensionsForDifference(productionSchedulePath: ProductionSchedulePath): string {
    let longestPathLengthInPicks = 0;
    let longestPathLengthInMM = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionScheduleBaan: ProductionSchedulePath) => {
        longestPathLengthInMM = Math.max(longestPathLengthInMM, productionScheduleBaan?.totalLengthInMM);
        longestPathLengthInPicks = Math.max(longestPathLengthInPicks, productionScheduleBaan?.totalLengthInPicks);
      });
    });

    if (AssertionUtils.isNullOrUndefined(productionSchedulePath.totalLengthInPicks)) {
      productionSchedulePath.totalLengthInPicks = 0;
    }

    if (AssertionUtils.isNullOrUndefined(productionSchedulePath.totalLengthInMM)) {
      productionSchedulePath.totalLengthInMM = 0;
    }

    const differenceFromLongestPathInPicks = longestPathLengthInPicks - productionSchedulePath?.totalLengthInPicks;
    const differenceFromLongestPathInCommercialUnit = this.convertValueToCommercialUnit(longestPathLengthInMM - productionSchedulePath?.totalLengthInMM, Unit.MILLIMETER);

    return this.getLabelForMaxLengthOrDifference(differenceFromLongestPathInPicks, differenceFromLongestPathInCommercialUnit);
  }

  public getUnitDimensionsForPathWidth(productionSchedulePath: ProductionSchedulePath): string {
    let widthInCommercialUnit = this.convertValueToCommercialUnit(productionSchedulePath.commercialWidthInMM, Unit.MILLIMETER);

    if (widthInCommercialUnit === 0) {
      widthInCommercialUnit = productionSchedulePath.technicalWidthInDents
        ? this.convertValueToCommercialUnit(convertDentsToWidthInMeter(productionSchedulePath.technicalWidthInDents, this.productionSchedule?.machineQuality?.reedDensity * 1000), Unit.METER)
        : 0;
    }

    return this.getLabelForWidth(widthInCommercialUnit, productionSchedulePath.technicalWidthInDents ?? 0);
  }

  public getUnitDimensionsForPathLength(productionSchedulePath: ProductionSchedulePath): string {
    let lengthInCommercialUnit = this.convertValueToCommercialUnit(productionSchedulePath.totalLengthInMM, Unit.MILLIMETER);

    if (lengthInCommercialUnit === 0) {
      lengthInCommercialUnit = productionSchedulePath.totalLengthInPicks
        ? this.convertValueToCommercialUnit(convertPicksToHeightInMeter(productionSchedulePath.totalLengthInPicks, this.productionSchedule?.machineQuality?.pickDensity * 1000), Unit.METER)
        : 0;
    }

    return this.getLabelForLength(lengthInCommercialUnit, productionSchedulePath.totalLengthInPicks ?? 0);
  }

  public openCadCam(target: HTMLElement, productionScheduleItem: ProductionScheduleItemInPathGroup): void {
    this.cadCamService.openCadCam(target, this.getExternalApplicationLink(productionScheduleItem), this.unSubscribeOnViewDestroy);
  }

  public showEditProductionScheduleItemDialog(
    productionScheduleColoredYarnSetIndex: number,
    productionSchedulePathIndex: number,
    productionScheduleItemIndex: number,
    productionScheduleItem: ProductionScheduleItemInPathGroup
  ): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('edit-drawing-in-path')
      .openDialog(EditProductionScheduleItemInPathGroupComponent, {
        productionScheduleItemInPathGroup: productionScheduleItem,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        allowExceedingRequestedAmountOfOrderLineItems: this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems,
        pickDensityInPicksPerMeter: this.productionSchedule.machineQuality.pickDensity * 1000
      })
      .pipe(
        filter((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => !AssertionUtils.isNullOrUndefined(result)),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => {
        if (!AssertionUtils.isNullOrUndefined(result.value)) {
          if (result.action === EditProductionScheduleItemInPathGroupAction.EDIT) {
            if (result.value.amountProduced !== 0) {
              result.value.status = ProductionScheduleItemInPathGroupStatus.CHANGED;
            }
          } else if (result.action === EditProductionScheduleItemInPathGroupAction.DELETE || result.action === EditProductionScheduleItemInPathGroupAction.DELETE_COMPLETING_PARTIALLY) {
            result.value.status = ProductionScheduleItemInPathGroupStatus.REMOVED;
            result.value.amountProduced = 0;
          }
          this.editQuantity(result.value.amountProduced, productionScheduleColoredYarnSetIndex, productionSchedulePathIndex, productionScheduleItemIndex);
          this.refreshRows();
        }
      });
  }

  public completePartially(): Observable<void> {
    this.productionSchedule.status = ProductionScheduleStatus.EXECUTED_PARTIAL;
    return this.productionOrders.completeProductionOrder(this.productionSchedule.id, this.productionSchedule);
  }

  public getHeightForExpansionPanel(productionSchedulePath: ProductionSchedulePath): number {
    let expanded = true;

    this.expansionPanels?.forEach((expansionPanel: MatExpansionPanel) => {
      const pathNumber = expansionPanel.id.slice(-1);
      if (Number(pathNumber) === productionSchedulePath.pathNumber - 1) {
        expanded = expansionPanel.expanded;
      }
    });

    if (expanded) {
      return productionSchedulePath?.pathGroups?.length * this.rowHeight + 2 * this.rowHeight + this.headerHeight + this.scrollBarHeightInPx;
    } else {
      return 40;
    }
  }

  private getDataForProductionScheduleItem(): void {
    const orderlineIds: number[] = [];

    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        productionSchedulePath.pathGroups.forEach((pathGroup: ProductionScheduleItemInPathGroup) => {
          if (AssertionUtils.isNullOrUndefined(pathGroup.id)) {
            pathGroup.id = Math.round(Math.random() * 200);
          }
          if (!AssertionUtils.isNullOrUndefined(pathGroup.orderLineId)) {
            orderlineIds.push(pathGroup.orderLineId);
          }
        });
      });
    });

    this.getOrderlines(orderlineIds);
  }

  private getExternalApplicationLink(productionScheduleItem: ProductionScheduleItemInPathGroup): Observable<string> {
    return this.profile
      .getExternalApplicationKey()
      .pipe(
        map(
          (externalApplicationKey: string) =>
            `txcccm://application_key:${externalApplicationKey}:${btoa(`{"cfgVersion":1, "context":"viewEp", "projectId": 0, "epId":${productionScheduleItem.epId}}`)}`
        )
      );
  }

  private getOrderlines(orderlineIds: number[]): void {
    if (!AssertionUtils.isNullOrUndefined(orderlineIds) && !AssertionUtils.isEmpty(orderlineIds)) {
      const orderlineIdsString = orderlineIds.map((id: number) => `${id}`);

      const filterModel = {
        id: {
          filterModels: [null, {values: orderlineIdsString, filterType: 'set'}],
          filterType: 'multi'
        }
      };

      const gridModel = new GridModel(0, orderlineIds.length, [], filterModel);

      this.orderbook
        .getSlice(OrderLinesTarget.PRODUCTION_SCHEDULE, gridModel)
        .pipe(
          map((orderLines: OverviewListOrderLine[]) =>
            orderLines.map((overviewListOrderLine: OverviewListOrderLine) =>
              ProductionScheduleOrderLineWithQuantity.fromProductionScheduleOrderLine(ProductionScheduleOrderLine.fromOverviewListOrderLine(overviewListOrderLine))
            )
          ),
          takeUntil(this.unSubscribeOnViewDestroy)
        )
        .subscribe((orderLines: ProductionScheduleOrderLineWithQuantity[]) => {
          this.orderlines = orderLines;
          this.setDataForEveryPath();
        });
    }
  }

  private setDataForEveryPath(): void {
    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        productionSchedulePath.pathGroups.forEach((pathGroup: ProductionScheduleItemInPathGroup) => {
          pathGroup.orderline = this.orderlines.find((orderline: ProductionScheduleOrderLine) => orderline.id === pathGroup.orderLineId);
        });
      });
    });

    this.refreshRows();
  }

  private refreshRows(): void {
    const listViewGridsArray = this.listViewGrids?.toArray();
    if (!AssertionUtils.isNullOrUndefined(listViewGridsArray)) {
      listViewGridsArray.forEach((grid: AgGridAngular) => {
        grid.api.redrawRows();
      });
    }
  }

  private initialiseGridOptionsForListiew(gridKeys: number[]): void {
    this.gridOptionsForListView = gridKeys.reduce(
      (gridMap: {[pathNumber: number]: GridOptions}, gridKey: number) => ({
        ...gridMap,
        [gridKey]: this.gridOptionsBuilderFactoryService
          .getBuilder(this.getColumnDefs(), GridIdentifier.PRODUCTION_SCHEDULE_PATH_LIST_VIEW + gridKey)
          .withHeaderHeight(this.rowHeight)
          .withFloatingFiltersHeight(this.rowHeight)
          .withCompactView()
          .withColumnMenu()
          .withPopupParent(document.querySelector('body'))
          .build()
      }),
      {}
    );
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withColId('rowId').withNumericMultiFilter().withHide().withHiddenInToolPanel().withoutFilter().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('drawingName')
        .withHeaderName('ARTICLES.ARTICLE', 1)
        .withField('orderline.article.name', true)
        .withComparator(StringUtils.stringComparator)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('status')
        .withHide(!this.productionSchedule.completingPartially && !this.productionSchedule.isPartiallyOrCompletelyExecuted())
        .withHiddenInToolPanel(!this.productionSchedule.completingPartially)
        .withField('status')
        .withHeaderName('GENERAL.STATUS')
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          return {
            component: ProductionSchedulePartiallyCompletedStatusCellRendererComponent,
            params: {productionScheduleItem: params.data} as ProductionSchedulePartiallyCompletedStatusParams
          };
        })
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('totalWovenHeightInPicks')
        .withHide(!this.productionSchedule.completingPartially && !this.productionSchedule.isPartiallyOrCompletelyExecuted())
        .withHiddenInToolPanel(!this.productionSchedule.completingPartially)
        .withField('totalWovenHeightInPicks')
        .withHeaderName('GENERAL.DIMENSIONS.TOTAL_WOVEN_HEIGHT', 1, false, Unit.PICKS)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('pieces')
        .withHeaderName('ORDERBOOK.QUANTITY')
        .withField('quantity')
        .withEditable(this.productionSchedule.completingPartially)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withCellEditor(
          NumberCellEditorComponent,
          (params: ICellEditorParams<RowData>): NumberCellEditorParams =>
            ({
              min: 0,
              max: 100000,
              autoSelect: true
            }) as NumberCellEditorParams
        )
        .withValueGetter((params: ValueGetterParams<RowData>) => this.getValueForQuantity(params), true)
        .withValueSetter((params: ValueSetterParams<ProductionScheduleItemInPathGroup>) => this.setQuantity(params))
        .withWidth(100)
        .withMinWidth(100)
        .withMaxWidth(150)
        .withHiddenInToolPanel()
        .withRightAlignment()
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designCode')
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_CODE')
        .withField('drawing.code', true)
        .withComparator(StringUtils.stringComparator)
        .withoutFilter()
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialWidthInCM')
        .withField('commercialDimensionsInMM.widthInMM')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) =>
          AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.commercialDimensionsInMM.widthInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService, 2)
        )
        .withTooltipValueGetter((params: ITooltipParams) =>
          AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService, undefined, 2)
        )
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialHeightInCM')
        .withField('commercialDimensionsInMM.heightInMM')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) =>
          AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.commercialDimensionsInMM.heightInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService, 2)
        )
        .withTooltipValueGetter((params: ITooltipParams) =>
          AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService, undefined, 2)
        )
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalWidthInDents')
        .withField('technicalDimensions.widthInDents')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.DENTS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.DENTS, this.l10nIntlService))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalHeightInPicks')
        .withField('technicalDimensions.heightInPicks')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.PICKS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PICKS, this.l10nIntlService))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('mainColor')
        .withField('borderColorPosition', false)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.MAIN_COLOR')
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true))
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('borderColor')
        .withField('borderColorPosition', false)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.BORDER_COLOR')
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true, false))
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('finishingName')
        .withField('finishing.nameWithVersion', true)
        .withHeaderName('TEXTILE_DATA.FINISHING.FINISHING')
        .withCellRendererSelector(this.cellRendererSelector(FinishingListViewInformationComponent, {}, false, false))
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('shape')
        .withField('drawing.shape', true)
        .withHeaderName('GENERAL.SHAPE')
        .withComparator(StringUtils.stringComparator)
        .withValueGetter((params: ValueGetterParams) => this.getShape(params.data?.drawing?.shape), true)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('salesOrderReference')
        .withHeaderName('ORDERBOOK.ORDERBOOK_REFERENCE')
        .withField('orderline.salesOrderReference', true)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('salesOrderlineReference')
        .withHeaderName('ORDERBOOK.ORDERLINE_REFERENCE')
        .withField('orderline.salesOrderLineReference', true)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('qualityName')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withField('orderline.article.machineQuality.nameWithVersion', true)
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('coloredYarnSetName')
        .withHeaderName('TEXTILE_DATA.COLORED_YARN_SET.COLORED_YARN_SET')
        .withField('orderline.article.coloredYarnSet.name', true)
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('articleName')
        .withHeaderName('DESIGN_LIBRARY.DETAILS.NAME', 1)
        .withField('drawing.name', true)
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('desiredDate')
        .withField('orderline.desiredDate')
        .withDate()
        .withHeaderName('SALES_ORDERS.DESIRED_DATE')
        .withDateMultiFilter()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('customerReference')
        .withField('orderline.customerReference', true)
        .withHeaderName('ORDERBOOK.CUSTOMER_REFERENCE')
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('priority')
        .withField('orderline.priority')
        .withHeaderName('SALES_ORDERS.PRIORITY.PRIORITY')
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          return !AssertionUtils.isNullOrUndefined(params.data?.orderline?.priority)
            ? {
                component: GridTagComponent,
                params: {
                  translationKey: 'SALES_ORDERS.PRIORITY.',
                  size: TagSize.SMALL,
                  colorGetter: {
                    [Priority.LOW]: TagColor.PRIMARY,
                    [Priority.MEDIUM]: TagColor.WARNING,
                    [Priority.HIGH]: TagColor.ERROR
                  }
                } as any
              }
            : null;
        })
        .withTooltipValueGetter((params: ITooltipParams) =>
          AssertionUtils.isNullOrUndefined(params.data?.orderline?.priority) ? null : this.translate.instant(`SALES_ORDERS.PRIORITY.${params.value}`).toUpperCase()
        )
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('open-in-cadcam')
        .withField('open-in-cadcam')
        .withHeaderName('')
        .withoutFilter()
        .withWidth(32)
        .withHide(!this.hasViewEpPermission || this.productionSchedule.completingPartially || this.productionSchedule.isPartiallyOrCompletelyExecuted())
        .withHiddenInToolPanel()
        .withMaxWidth(32)
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          return {
            component: CellButtonComponent,
            params: {
              buttons: [{icon: 'solid-open-in-new', title: 'GENERAL.ACTIONS.OPEN_IN_CAD_CAM', onClick: () => this.openCadCam(params.eGridCell, params.data)} as CrudOverviewButtonConfig]
            } as CellButtonParams
          };
        })
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('more-actions')
        .withField('actions')
        .withHeaderName('')
        .withoutFilter()
        .withWidth(32)
        .withHide(!this.productionSchedule.completingPartially && !this.productionSchedule.isPartiallyOrCompletelyExecuted())
        .withHiddenInToolPanel()
        .withMaxWidth(32)
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          const buttons: CrudOverviewButtonConfig[] = [
            {
              icon: 'solid-edit',
              title: 'ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.EDIT',
              onClick: () =>
                this.showEditProductionScheduleItemDialog(
                  this.getCorrectIndex(params.data, 'productionScheduleColoredYarnSetIndex'),
                  this.getCorrectIndex(params.data, 'productionSchedulePathIndex'),
                  this.getCorrectIndex(params.data, 'productionScheduleItemIndex'),
                  params.data
                )
            } as CrudOverviewButtonConfig
          ];

          if (this.hasViewEpPermission) {
            buttons.push({
              icon: 'solid-open-in-new',
              title: 'GENERAL.ACTIONS.OPEN_IN_CAD_CAM',
              onClick: () => this.openCadCam(params.eGridCell, params.data)
            } as CrudOverviewButtonConfig);
          }

          return {
            component: CellButtonComponent,
            params: {buttons} as CellButtonParams
          };
        })
        .build()
    ];
  }

  private getValueForQuantity(params: ValueGetterParams<RowData> | ITooltipParams<RowData>): string {
    if (params.data instanceof ProductionScheduleItemInPathGroup) {
      const nrClothsMultiplier = this.productionSchedule?.getProductionScheduleItemMultiplier() ?? 2;
      if ((this.productionSchedule?.completingPartially || this.productionSchedule?.isPartiallyOrCompletelyExecuted()) && !AssertionUtils.isNullOrUndefined(params.data.amountProduced)) {
        return `${params.data?.amountProduced * nrClothsMultiplier}/${params.data?.quantity * nrClothsMultiplier}`;
      } else {
        return `${params.data?.quantity * nrClothsMultiplier}`;
      }
    }
  }

  private editQuantity(quantity: number, productionScheduleColoredYarnSetIndex: number, productionSchedulePathIndex: number, productionScheduleItemIndex: number): void {
    if (!AssertionUtils.isNullOrUndefined(quantity) && quantity >= 0) {
      const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.productionSchedulePathsOfColoredYarnSets[productionScheduleColoredYarnSetIndex];
      const productionScheduleItem = productionSchedulePathsOfColoredYarnSet.productionSchedulePaths[productionSchedulePathIndex].pathGroups[productionScheduleItemIndex];
      this.updateProductionScheduleItemEditableQuantityProperty(quantity, productionScheduleItem, productionScheduleColoredYarnSetIndex, productionSchedulePathIndex, productionScheduleItemIndex);
    }
  }

  private updateProductionScheduleItemEditableQuantityProperty(
    value: number,
    productionScheduleItem: ProductionScheduleItemInPathGroup,
    productionScheduleColoredYarnSetIndex: number,
    productionSchedulePathIndex: number,
    productionScheduleItemIndex: number
  ): void {
    if (this.productionSchedule.completingPartially) {
      const productionSchedulePathsOfColoredYarnSet = this.initialProductionSchedule.productionSchedulePathsOfColoredYarnSets[productionScheduleColoredYarnSetIndex];
      const productionScheduleItemFromInitialProductionSchedule = productionSchedulePathsOfColoredYarnSet.productionSchedulePaths[productionSchedulePathIndex].pathGroups[productionScheduleItemIndex];
      productionScheduleItem.amountProduced = value;

      if (productionScheduleItemFromInitialProductionSchedule.amountProduced === value) {
        productionScheduleItem.status = ProductionScheduleItemInPathGroupStatus.NORMAL;
      } else if (value === 0) {
        productionScheduleItem.status = ProductionScheduleItemInPathGroupStatus.REMOVED;
      } else {
        productionScheduleItem.status = ProductionScheduleItemInPathGroupStatus.CHANGED;
      }
    } else {
      productionScheduleItem.quantity = Math.floor(value / this.productionSchedule.getProductionScheduleItemMultiplier());
    }
  }

  private editQuantityCell(event: CellClickedEvent): void {
    if (event?.api) {
      event?.api?.stopEditing();
    }

    if (event.column.getColDef().editable) {
      event?.api.startEditingCell({
        rowIndex: event?.rowIndex,
        colKey: event?.column?.getColId()
      });
    }
  }

  private setQuantity(params: ValueSetterParams<ProductionScheduleItemInPathGroup>): boolean {
    if (this.productionScheduleItemMultiplier === 2 && params.newValue % 2 !== 0) {
      params.newValue--;
    }
    params.node.data.amountProduced = Math.max(0, params.newValue);

    this.editQuantity(
      params.newValue / 2,
      this.getCorrectIndex(params.data, 'productionScheduleColoredYarnSetIndex'),
      this.getCorrectIndex(params.data, 'productionSchedulePathIndex'),
      this.getCorrectIndex(params.data, 'productionScheduleItemIndex')
    );

    return true;
  }

  private getDateModified(item: ProductionScheduleItemInPathGroup): Date {
    return item?.orderline?.desiredDate;
  }

  private getCorrectIndex(productionScheduleItem: ProductionScheduleItemInPathGroup, indexOf: string): number {
    let index: number;
    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, coloredYarnSetIndex: number) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath, pathIndex: number) => {
        productionSchedulePath.pathGroups.forEach((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup, itemIndex: number) => {
          if (productionScheduleItemInPathGroup.id === productionScheduleItem.id) {
            if (indexOf === 'productionScheduleColoredYarnSetIndex') {
              index = coloredYarnSetIndex;
            } else if (indexOf === 'productionSchedulePathIndex') {
              index = pathIndex;
            } else if (indexOf === 'productionScheduleItemIndex') {
              index = itemIndex;
            }
          }
        });
      });
    });

    return index;
  }

  private getMainColor(rowItem: ProductionScheduleItemInPathGroup): Color {
    const coloredYarnSet = this.getColoredYarnSetForPathItem(rowItem);
    return coloredYarnSet?.coloredYarnSet?.overviewListColorSet?.creelPositions[rowItem.mainColorPosition - 1]?.items[0] as Color;
  }

  private getBorderColor(rowItem: ProductionScheduleItemInPathGroup): Color {
    const coloredYarnSet = this.getColoredYarnSetForPathItem(rowItem);
    return coloredYarnSet?.coloredYarnSet?.overviewListColorSet?.creelPositions[rowItem.borderColorPosition - 1]?.items[0] as Color;
  }

  private getColoredYarnSetForPathItem(rowItem: ProductionScheduleItemInPathGroup): OverviewListColoredYarnSetWithStartDent {
    let coloredYarnSet: OverviewListColoredYarnSetWithStartDent;

    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathOfColoredYarnSets: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathOfColoredYarnSets.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        productionSchedulePath.pathGroups.forEach((pathItem: ProductionScheduleItemInPathGroup) => {
          if (pathItem.id === rowItem.id) {
            coloredYarnSet = productionSchedulePathOfColoredYarnSets.productionScheduleColoredYarnSet;
          }
        });
      });
    });

    return coloredYarnSet;
  }

  private cellRendererSelector<TData = any, TValue = any>(
    cellRenderer: any = null,
    cellRendererParams: any = null,
    cellForMainOrBorderColor: boolean = false,
    useMainColor: boolean = true
  ): (params: ICellRendererParams<TData, TValue>) => CellRendererSelectorResult {
    return (params: ICellRendererParams<TData, TValue>): CellRendererSelectorResult => {
      if (cellForMainOrBorderColor) {
        const rowItem = params.data as ProductionScheduleItemInPathGroup;
        const mainColor = this.getMainColor(rowItem);
        const borderColor = this.getBorderColor(rowItem);
        cellRendererParams.value = {colors: useMainColor ? [mainColor] : [borderColor]};
      }
      return params.node.level === 0 ? {component: cellRenderer ?? params.colDef.cellRenderer, params: cellRendererParams} : {component: EmptyCellRendererComponent};
    };
  }

  private getShape(shape: string): string {
    return FixedShape.getTranslationKeyForShape(shape, this.translate);
  }

  private getLabelForMaxLengthOrDifference(technicalHeightInPicks: number, commercialHeightInCommercialUnit: number): string {
    return `${commercialHeightInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)} - ${technicalHeightInPicks} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.PICKS)}`
    )}`;
  }

  private getLabelForLength(commercialLengthInCommercialUnit: number, totalLengthInPicks: number): string {
    return `${commercialLengthInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)} - ${totalLengthInPicks} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.PICKS)}`
    )}`;
  }

  private getLabelForWidth(commercialWidthInCommercialUnit: number, technicalWidthInDents: number): string {
    return `${commercialWidthInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)} - ${technicalWidthInDents} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.DENTS)}`
    )}`;
  }

  private convertValueToCommercialUnit(value: number, unit: Unit): number {
    return round(
      convertCommercialUnit({
        from: {value: value ?? 0, unit},
        to: this.commercialUnit
      }),
      0
    );
  }

  private getPathNumbers(productionSchedule: ProductionSchedule): number[] {
    return (
      productionSchedule?.productionSchedulePathsOfColoredYarnSets?.flatMap(
        (pathsOfYarnSets: ProductionSchedulePathsOfColoredYarnSet) => pathsOfYarnSets.productionSchedulePaths?.map((path: ProductionSchedulePath) => path.pathNumber) ?? []
      ) ?? []
    );
  }
}
